import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ApartmentIcon from "@mui/icons-material/Apartment";
import SchoolIcon from "@mui/icons-material/School";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import GradeIcon from "@mui/icons-material/Grade";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { API_URL } from "./config";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const RESULT_STATUS_OPTIONS = ["Pass", "Fail", "Absent", "Withheld"];

const MASTER_CONFIGS = [
  {
    key: "subcampus",
    label: "Sub Campus",
    title: "Sub Campus",
    description: "Maintain sub-campus codes and campus names.",
    endpoint: "CodeSubCampus",
    uploadEndpoint: "UploadCodeSubCampus",
    sheetName: "CodeSubCampus",
    sampleFile: "CodeSubCampus_Sample",
    icon: <ApartmentIcon />,
    fields: [
      { key: "SubCampusCode", label: "Sub Campus Code", required: true },
      { key: "CampusName", label: "Campus Name", required: true },
    ],
    initial: { SubCampusCode: "", CampusName: "", IsActive: 1 },
    sampleRows: (cc) => [
      { CampusCode: cc, SubCampusCode: "SC01", CampusName: "Main Campus", IsActive: 1 },
      { CampusCode: cc, SubCampusCode: "SC02", CampusName: "City Campus", IsActive: 1 },
    ],
    instructions: [
      ["SubCampusCode", "Required. Unique within the campus. Example: SC01"],
      ["CampusName", "Human-readable campus name."],
      ["IsActive", "1 = Active, 0 = Inactive"],
    ],
  },
  {
    key: "degree",
    label: "Degree",
    title: "Degree",
    description: "Maintain degree master data for each sub-campus.",
    endpoint: "CodeDegree",
    uploadEndpoint: "UploadCodeDegree",
    sheetName: "CodeDegree",
    sampleFile: "CodeDegree_Sample",
    icon: <SchoolIcon />,
    fields: [
      { key: "SubCampusCode", label: "Sub Campus", required: true, lookup: "SubCampusCode" },
      { key: "DegreeCode", label: "Degree Code", required: true },
      { key: "DegreeName", label: "Degree Name", required: true },
    ],
    filters: [
      { key: "SubCampusCode", label: "Sub Campus", lookup: "SubCampusCode" },
      { key: "DegreeCode", label: "Degree", lookup: "DegreeCode" },
      { key: "IsActive", label: "Status", values: ["Active", "Inactive"] },
    ],
    initial: { SubCampusCode: "", DegreeCode: "", DegreeName: "", IsActive: 1 },
    sampleRows: (cc) => [
      { CampusCode: cc, SubCampusCode: "SC01", DegreeCode: "BTECH", DegreeName: "B.Tech", IsActive: 1 },
      { CampusCode: cc, SubCampusCode: "SC01", DegreeCode: "MBA", DegreeName: "MBA", IsActive: 1 },
    ],
    instructions: [
      ["SubCampusCode", "Must already exist for this campus."],
      ["DegreeCode", "Required. Example: BTECH, MTECH, MBA"],
      ["DegreeName", "Human-readable degree name."],
      ["IsActive", "1 = Active, 0 = Inactive"],
    ],
  },
  {
    key: "branch",
    label: "Branch",
    title: "Branch",
    description: "Maintain branch master data for each sub-campus and degree.",
    endpoint: "CodeCampusBranch",
    uploadEndpoint: "UploadCodeCampusBranch",
    sheetName: "CodeCampusBranch",
    sampleFile: "CodeCampusBranch_Sample",
    icon: <AccountTreeIcon />,
    fields: [
      { key: "SubCampusCode", label: "Sub Campus", required: true, lookup: "SubCampusCode" },
      { key: "DegreeCode", label: "Degree", required: true, lookup: "DegreeCode" },
      { key: "BranchCode", label: "Branch Code", required: true },
      { key: "BranchName", label: "Branch Name", required: true },
    ],
    filters: [
      { key: "SubCampusCode", label: "Sub Campus", lookup: "SubCampusCode" },
      { key: "DegreeCode", label: "Degree", lookup: "DegreeCode" },
      { key: "BranchCode", label: "Branch", lookup: "BranchCode" },
      { key: "IsActive", label: "Status", values: ["Active", "Inactive"] },
    ],
    initial: { SubCampusCode: "", DegreeCode: "", BranchCode: "", BranchName: "", IsActive: 1 },
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        SubCampusCode: "SC01",
        DegreeCode: "BTECH",
        BranchCode: "CSE",
        BranchName: "Computer Science and Engineering",
        IsActive: 1,
      },
      {
        CampusCode: cc,
        SubCampusCode: "SC01",
        DegreeCode: "BTECH",
        BranchCode: "ECE",
        BranchName: "Electronics and Communication Engineering",
        IsActive: 1,
      },
    ],
    instructions: [
      ["SubCampusCode", "Must already exist for this campus."],
      ["DegreeCode", "Must already exist for the selected sub-campus."],
      ["BranchCode", "Required. Example: CSE, ECE, IT"],
      ["BranchName", "Full branch name."],
      ["IsActive", "1 = Active, 0 = Inactive"],
    ],
  },
  {
    key: "semester",
    label: "Semester",
    title: "Semester",
    description: "Maintain semester codes and display order.",
    endpoint: "Semesters",
    uploadEndpoint: "UploadSemesters",
    sheetName: "Semesters",
    sampleFile: "Semesters_Sample",
    icon: <CalendarMonthIcon />,
    fields: [
      { key: "SemesterCode", label: "Semester Code", required: true },
      { key: "SemesterOrder", label: "Order", required: true, type: "number" },
    ],
    initial: { SemesterCode: "", SemesterOrder: "", IsActive: 1 },
    sampleRows: (cc) => [
      { CampusCode: cc, SemesterCode: "1", SemesterOrder: 1, IsActive: 1 },
      { CampusCode: cc, SemesterCode: "2", SemesterOrder: 2, IsActive: 1 },
      { CampusCode: cc, SemesterCode: "3", SemesterOrder: 3, IsActive: 1 },
    ],
    instructions: [
      ["SemesterCode", "Semester identifier such as 1, 2, 3 or SEM1."],
      ["SemesterOrder", "Numeric display order."],
      ["IsActive", "1 = Active, 0 = Inactive"],
    ],
  },
  {
    key: "grades",
    label: "Grades",
    title: "Grades",
    description: "Configure grade codes, ranges, grade points and result status.",
    endpoint: "CodeGrades",
    uploadEndpoint: "UploadCodeGrades",
    sheetName: "CodeGrades",
    sampleFile: "CodeGrades_Sample",
    icon: <GradeIcon />,
    fields: [
      { key: "GradeCode", label: "Grade Code", required: true },
      { key: "GradeCodeOrderBy", label: "Order", type: "number" },
      { key: "Grade", label: "Grade Name" },
      { key: "GradeRange", label: "Range" },
      { key: "GradeLevel", label: "Level" },
      { key: "GradePoint", label: "Grade Point", type: "number" },
      { key: "ResultStatus", label: "Result Status", values: RESULT_STATUS_OPTIONS },
    ],
    initial: {
      GradeCode: "",
      GradeCodeOrderBy: "",
      Grade: "",
      GradeRange: "",
      GradeLevel: "",
      GradePoint: "",
      ResultStatus: "",
      IsActive: 1,
    },
    sampleRows: (cc) => [
      {
        CampusCode: cc,
        GradeCode: "A+",
        GradeCodeOrderBy: 1,
        Grade: "Excellent",
        GradeRange: "90-100",
        GradeLevel: "Distinction",
        GradePoint: 10,
        ResultStatus: "Pass",
        IsActive: 1,
      },
      {
        CampusCode: cc,
        GradeCode: "F",
        GradeCodeOrderBy: 9,
        Grade: "Fail",
        GradeRange: "0-39",
        GradeLevel: "Fail",
        GradePoint: 0,
        ResultStatus: "Fail",
        IsActive: 1,
      },
      {
        CampusCode: cc,
        GradeCode: "AB",
        GradeCodeOrderBy: 98,
        Grade: "Absent",
        GradeRange: "",
        GradeLevel: "",
        GradePoint: 0,
        ResultStatus: "Absent",
        IsActive: 1,
      },
      {
        CampusCode: cc,
        GradeCode: "WH",
        GradeCodeOrderBy: 99,
        Grade: "Withheld",
        GradeRange: "",
        GradeLevel: "",
        GradePoint: 0,
        ResultStatus: "Withheld",
        IsActive: 1,
      },
    ],
    instructions: [
      ["GradeCode", "Short grade code such as A+, A, B, F, AB, WH."],
      ["GradeCodeOrderBy", "Numeric display order."],
      ["GradeRange", "Marks range such as 90-100."],
      ["GradePoint", "Numeric grade point."],
      ["ResultStatus", "Pass / Fail / Absent / Withheld"],
      ["IsActive", "1 = Active, 0 = Inactive"],
    ],
  },
];

const makeInitialRows = (campusCode = "") =>
  Object.fromEntries(
    MASTER_CONFIGS.map((cfg) => [
      cfg.key,
      { CampusCode: campusCode, ...cfg.initial },
    ])
  );

const makeEmptyFilters = () =>
  Object.fromEntries(
    MASTER_CONFIGS.map((cfg) => [
      cfg.key,
      Object.fromEntries((cfg.filters || []).map((f) => [f.key, []])),
    ])
  );

export default function AcademicCodeSetup() {
  // Theme copied from FormStudentAssignment
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";
  const BLUE = "#1565C0";

  const token = localStorage.getItem("auth") || localStorage.getItem("token");
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const [tab, setTab] = useState(0);
  const [campusCode, setCampusCode] = useState("");
  const [rows, setRows] = useState(
    Object.fromEntries(MASTER_CONFIGS.map((c) => [c.key, []]))
  );
  const [newRows, setNewRows] = useState(makeInitialRows(""));
  const [filters, setFilters] = useState(makeEmptyFilters());
  const [editState, setEditState] = useState({ key: null, id: null });
  const [lookups, setLookups] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [msg, setMsg] = useState({ type: "success", text: "" });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [pageByKey, setPageByKey] = useState(
    Object.fromEntries(MASTER_CONFIGS.map((c) => [c.key, 0]))
  );
  const [rowsPerPageByKey, setRowsPerPageByKey] = useState(
    Object.fromEntries(MASTER_CONFIGS.map((c) => [c.key, 25]))
  );
  const fileInputs = useRef({});

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
  };
  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };
  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg,${THEME_ORANGE},${THEME_ORANGE_2})`,
    },
  };
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: BORDER },
      "&:hover fieldset": { borderColor: BORDER_HOVER },
      "&.Mui-focused fieldset": { borderColor: BORDER_FOCUS },
    },
  };
  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };
  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 800,
    borderColor: "rgba(25,118,210,0.35)",
    color: BLUE,
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };
  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": { color: "#fff", fontWeight: 900, whiteSpace: "nowrap" },
  };
  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#fff",
    "&:hover": { backgroundColor: ROW_HOVER },
  });
  const iconBtnSx = {
    color: BLUE,
    "&:hover": {
      backgroundColor: "rgba(240,120,40,0.10)",
      color: THEME_ORANGE,
    },
  };
  const tabsSx = {
    px: 1,
    borderBottom: `1px solid ${BORDER}`,
    minHeight: 48,
    "& .MuiTab-root": {
      textTransform: "none",
      fontWeight: 800,
      minHeight: 48,
    },
    "& .Mui-selected": {
      color: `${THEME_ORANGE} !important`,
    },
    "& .MuiTabs-indicator": {
      backgroundColor: THEME_ORANGE,
      height: 3,
    },
  };

  const activeConfig = MASTER_CONFIGS[tab];
  const activePage = pageByKey[activeConfig.key] || 0;
  const activeRowsPerPage = rowsPerPageByKey[activeConfig.key] || 25;

  const openAddDialog = (cfg = activeConfig) => {
    setNewRows((prev) => ({
      ...prev,
      [cfg.key]: { CampusCode: campusCode, ...cfg.initial },
    }));
    setAddDialogOpen(true);
  };

  const closeAddDialog = () => setAddDialogOpen(false);

  useEffect(() => {
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      const cc = decoded.campusCode || decoded.CampusCode || "";
      setCampusCode(cc);
      setNewRows(makeInitialRows(cc));
    } catch {
      setMsg({ type: "error", text: "Failed to decode authentication token." });
    }
  }, [token]);

  const buildParams = (cfg) => {
    const current = filters[cfg.key] || {};
    const params = {};
    (cfg.filters || []).forEach((f) => {
      const selected = current[f.key] || [];
      if (f.key === "IsActive") {
        params.IsActive =
          selected.length === 0
            ? "1,0"
            : selected.map((x) => (x === "Active" ? 1 : 0)).join(",");
      } else if (selected.length) {
        params[f.key] = selected.join(",");
      }
    });
    return params;
  };

  const fetchData = async (cfg, { silent = false } = {}) => {
    if (!campusCode) return;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/${cfg.endpoint}`, {
        headers: authHeaders,
        params: buildParams(cfg),
      });
      setRows((prev) => ({
        ...prev,
        [cfg.key]: Array.isArray(response.data) ? response.data : [],
      }));
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || `Failed to load ${cfg.label}.`,
      });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!campusCode) return;
    fetchData(activeConfig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campusCode, tab, filters[activeConfig.key]]);

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;
    (async () => {
      setLoadingLookups(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          headers: authHeaders,
          params: { CampusCode: campusCode },
        });
        if (cancelled) return;
        setLookups({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch {
        // Fallback below refreshes lookup values from the master endpoints.
        try {
          const [sc, degree, branch, semester] = await Promise.all([
            axios.get(`${API_URL}/api/CodeSubCampus`, { headers: authHeaders }),
            axios.get(`${API_URL}/api/CodeDegree`, { headers: authHeaders }),
            axios.get(`${API_URL}/api/CodeCampusBranch`, { headers: authHeaders }),
            axios.get(`${API_URL}/api/Semesters`, { headers: authHeaders }),
          ]);
          if (cancelled) return;
          const uniq = (a) => [...new Set(a.filter(Boolean))];
          setLookups({
            SubCampusCode: uniq((sc.data || []).map((x) => x.SubCampusCode)),
            DegreeCode: uniq((degree.data || []).map((x) => x.DegreeCode)),
            BranchCode: uniq((branch.data || []).map((x) => x.BranchCode)),
            SemesterCode: uniq((semester.data || []).map((x) => String(x.SemesterCode))),
          });
        } catch {
          if (!cancelled) {
            setMsg({ type: "error", text: "Failed to load academic setup dropdowns." });
          }
        }
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campusCode, authHeaders]);

  const refreshLookups = async () => {
    if (!campusCode) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
        headers: authHeaders,
        params: { CampusCode: campusCode },
      });
      setLookups({
        SubCampusCode: data?.SubCampusCode || [],
        DegreeCode: data?.DegreeCode || [],
        BranchCode: data?.BranchCode || [],
        SemesterCode: (data?.SemesterCode || []).map(String),
      });
    } catch {
      // Non-blocking: CRUD itself has already succeeded.
    }
  };

  const updateNewRow = (cfg, field, value) => {
    setNewRows((prev) => ({
      ...prev,
      [cfg.key]: { ...prev[cfg.key], [field]: value },
    }));
  };

  const updateExistingRow = (cfg, id, field, value) => {
    setRows((prev) => ({
      ...prev,
      [cfg.key]: (prev[cfg.key] || []).map((r) =>
        r.id === id ? { ...r, [field]: value } : r
      ),
    }));
  };

  const addRow = async (cfg) => {
    const payload = { ...newRows[cfg.key], CampusCode: campusCode };
    for (const field of cfg.fields) {
      if (field.required && String(payload[field.key] ?? "").trim() === "") {
        setMsg({ type: "error", text: `${field.label} is required.` });
        return;
      }
    }
    try {
      await axios.post(`${API_URL}/api/${cfg.endpoint}`, payload, {
        headers: authHeaders,
      });
      setNewRows((prev) => ({
        ...prev,
        [cfg.key]: { CampusCode: campusCode, ...cfg.initial },
      }));
      setMsg({ type: "success", text: `${cfg.label} added successfully.` });
      setAddDialogOpen(false);
      await fetchData(cfg, { silent: true });
      await refreshLookups();
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || `Failed to add ${cfg.label}.`,
      });
    }
  };

  const saveRow = async (cfg, row) => {
    try {
      await axios.put(`${API_URL}/api/${cfg.endpoint}/${row.id}`, row, {
        headers: authHeaders,
      });
      setEditState({ key: null, id: null });
      setMsg({ type: "success", text: `${cfg.label} updated successfully.` });
      await fetchData(cfg, { silent: true });
      await refreshLookups();
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || `Failed to update ${cfg.label}.`,
      });
    }
  };

  const deleteRow = async (cfg, row) => {
    if (!window.confirm(`Delete this ${cfg.label} record?`)) return;
    try {
      await axios.delete(`${API_URL}/api/${cfg.endpoint}/${row.id}`, {
        headers: authHeaders,
      });
      setMsg({ type: "success", text: `${cfg.label} deleted successfully.` });
      await fetchData(cfg, { silent: true });
      await refreshLookups();
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || `Failed to delete ${cfg.label}.`,
      });
    }
  };

  const downloadSample = (cfg) => {
    const sampleRows = cfg.sampleRows(campusCode || "CAMPUS01");
    const headers = Object.keys(sampleRows[0] || {});
    const ws = XLSX.utils.json_to_sheet(sampleRows, { header: headers });
    ws["!cols"] = headers.map((h) => ({ wch: Math.max(14, h.length + 2) }));

    const instructions = [
      ["Field / Rule", "Instruction"],
      ["Sheet Name", `Keep the sheet name as: ${cfg.sheetName}`],
      ["CampusCode", "Auto-filled from login. Keep the same CampusCode for all rows."],
      ...(cfg.instructions || []),
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(instructions);
    ws2["!cols"] = [{ wch: 24 }, { wch: 80 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, cfg.sheetName);
    XLSX.utils.book_append_sheet(wb, ws2, "Instructions");

    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${cfg.sampleFile}_${campusCode || "CAMPUS"}.xlsx`);
  };

  const handleBulkUpload = async (cfg, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/${cfg.uploadEndpoint}`, form, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      setMsg({ type: "success", text: `${cfg.label} bulk upload completed.` });
      await fetchData(cfg, { silent: true });
      await refreshLookups();
    } catch (e) {
      setMsg({
        type: "error",
        text: e.response?.data?.error || `${cfg.label} bulk upload failed.`,
      });
    } finally {
      setLoading(false);
    }
  };

  const updateFilter = (cfg, field, value) => {
    setFilters((prev) => ({
      ...prev,
      [cfg.key]: {
        ...(prev[cfg.key] || {}),
        [field]: value,
      },
    }));
    setPageByKey((prev) => ({ ...prev, [cfg.key]: 0 }));
  };

  const renderField = (cfg, field, value, onChange, { editing = false } = {}) => {
    const common = {
      fullWidth: true,
      size: "small",
      label: field.label,
      sx: inputSx,
    };

    if (field.values) {
      return (
        <TextField
          {...common}
          select
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          <MenuItem value="">
            <em>Select</em>
          </MenuItem>
          {field.values.map((x) => (
            <MenuItem key={x} value={x}>
              {x}
            </MenuItem>
          ))}
        </TextField>
      );
    }

    if (field.lookup) {
      return (
        <Autocomplete
          freeSolo={editing}
          options={(lookups[field.lookup] || []).map(String)}
          value={value == null ? "" : String(value)}
          onChange={(_, v) => onChange(v || "")}
          onInputChange={(_, v, reason) => {
            if (editing && reason === "input") onChange(v);
          }}
          loading={loadingLookups}
          renderInput={(params) => (
            <TextField {...params} {...common} />
          )}
        />
      );
    }

    return (
      <TextField
        {...common}
        type={field.type || "text"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  const renderFilters = (cfg) => {
    if (!cfg.filters?.length) return null;
    const current = filters[cfg.key] || {};
    return (
      <Paper elevation={0} sx={sectionSx}>
        <Typography fontWeight={900} color={BLUE} mb={1.5}>
          Filters
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(180px, 1fr))",
              md: `repeat(${Math.min(cfg.filters.length, 4)}, minmax(180px, 1fr))`,
            },
            gap: 1.5,
          }}
        >
          {cfg.filters.map((f) => {
            const options = f.values || lookups[f.lookup] || [];
            return (
              <Autocomplete
                key={f.key}
                multiple
                size="small"
                options={options.map(String)}
                value={(current[f.key] || []).map(String)}
                onChange={(_, v) => updateFilter(cfg, f.key, v)}
                renderInput={(params) => (
                  <TextField {...params} label={f.label} sx={inputSx} />
                )}
              />
            );
          })}
        </Box>
      </Paper>
    );
  };

  const renderTab = (cfg) => {
    const currentRows = rows[cfg.key] || [];
    const page = pageByKey[cfg.key] || 0;
    const rowsPerPage = rowsPerPageByKey[cfg.key] || 25;
    const maxPage = Math.max(0, Math.ceil(currentRows.length / rowsPerPage) - 1);
    const safePage = Math.min(page, maxPage);
    const paginatedRows = currentRows.slice(
      safePage * rowsPerPage,
      safePage * rowsPerPage + rowsPerPage
    );

    return (
      <Stack spacing={2}>
        {renderFilters(cfg)}

        <Paper elevation={0} sx={{ ...surfaceSx, overflow: "hidden" }}>
          <Box sx={{ overflowX: "auto" }}>
            <TableContainer>
              <Table size="small" sx={{ minWidth: Math.max(720, cfg.fields.length * 170) }}>
                <TableHead>
                  <TableRow sx={theadRowSx}>
                    <TableCell>#</TableCell>
                    {cfg.fields.map((f) => (
                      <TableCell key={f.key}>{f.label}</TableCell>
                    ))}
                    <TableCell>Active</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedRows.map((row, index) => {
                    const isEdit =
                      editState.key === cfg.key && editState.id === row.id;
                    return (
                      <TableRow key={row.id ?? `${cfg.key}-${index}`} sx={zebraRowSx(index)}>
                        <TableCell>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: "50%",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: 900,
                              fontSize: "1rem",
                              color: "#1976D2",
                              backgroundColor: "rgba(25,118,210,0.06)",
                              border: "2px solid rgba(25,118,210,0.20)",
                              lineHeight: 1,
                            }}
                          >
                            {safePage * rowsPerPage + index + 1}
                          </Box>
                        </TableCell>

                        {cfg.fields.map((field) => (
                          <TableCell key={field.key} sx={{ minWidth: 150 }}>
                            {isEdit
                              ? renderField(
                                  cfg,
                                  field,
                                  row[field.key],
                                  (v) =>
                                    updateExistingRow(
                                      cfg,
                                      row.id,
                                      field.key,
                                      v
                                    ),
                                  { editing: true }
                                )
                              : String(row[field.key] ?? "")}
                          </TableCell>
                        ))}

                        <TableCell>
                          {isEdit ? (
                            <Checkbox
                              checked={Number(row.IsActive) === 1}
                              onChange={(e) =>
                                updateExistingRow(
                                  cfg,
                                  row.id,
                                  "IsActive",
                                  e.target.checked ? 1 : 0
                                )
                              }
                              sx={{
                                color: BLUE,
                                "&.Mui-checked": { color: THEME_ORANGE },
                              }}
                            />
                          ) : (
                            <Checkbox
                              checked={Number(row.IsActive) === 1}
                              disabled
                              sx={{
                                p: 0.5,
                                color: BLUE,
                                "&.Mui-checked": { color: THEME_ORANGE },
                                "&.Mui-disabled": {
                                  color:
                                    Number(row.IsActive) === 1
                                      ? `${THEME_ORANGE} !important`
                                      : "rgba(25,118,210,0.35) !important",
                                },
                              }}
                            />
                          )}
                        </TableCell>

                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          {isEdit ? (
                            <>
                              <IconButton
                                size="small"
                                sx={iconBtnSx}
                                onClick={() => saveRow(cfg, row)}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                sx={iconBtnSx}
                                onClick={() =>
                                  setEditState({ key: null, id: null })
                                }
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                sx={iconBtnSx}
                                onClick={() =>
                                  setEditState({ key: cfg.key, id: row.id })
                                }
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteRow(cfg, row)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!loading && paginatedRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={cfg.fields.length + 3}>
                        <Typography
                          sx={{
                            p: 2,
                            color: "text.secondary",
                            textAlign: "center",
                          }}
                        >
                          No {cfg.label.toLowerCase()} records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell
                      colSpan={cfg.fields.length + 3}
                      sx={{
                        p: 0,
                        borderBottom: 0,
                        backgroundColor: "rgba(25,118,210,.025)",
                      }}
                    >
                      <Button
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={() => openAddDialog(cfg)}
                        sx={{
                          justifyContent: "center",
                          py: 1.35,
                          borderRadius: 0,
                          textTransform: "none",
                          fontWeight: 900,
                          color: THEME_ORANGE,
                          borderTop: `1px solid ${BORDER}`,
                          backgroundColor: "rgba(240,120,40,.035)",
                          "&:hover": {
                            backgroundColor: "rgba(240,120,40,.085)",
                          },
                        }}
                      >
                        Add {cfg.label}
                      </Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          <TablePagination
            component="div"
            count={currentRows.length}
            page={safePage}
            onPageChange={(_, p) =>
              setPageByKey((prev) => ({ ...prev, [cfg.key]: p }))
            }
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              const next = parseInt(e.target.value, 10);
              setRowsPerPageByKey((prev) => ({
                ...prev,
                [cfg.key]: next,
              }));
              setPageByKey((prev) => ({ ...prev, [cfg.key]: 0 }));
            }}
            rowsPerPageOptions={[10, 25, 50, 100, 200, 300]}
            labelRowsPerPage="Rows per page"
            showFirstButton
            showLastButton
          />
        </Paper>
      </Stack>
    );
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box
          sx={{
            ...headerSx,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900 }}>
              Academic Setup
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: ".85rem" }}>
              Maintain Sub Campus, Degree, Branch, Semester and Grade master data.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openAddDialog(activeConfig)}
              sx={{
                ...primaryBtnSx,
                backgroundColor: "white",
                color: THEME_ORANGE,
                "&:hover": { backgroundColor: "rgba(255,255,255,.92)" },
              }}
            >
              Add {activeConfig.label}
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => downloadSample(activeConfig)}
              sx={{
                ...outlinedBtnSx,
                backgroundColor: "white",
              }}
            >
              Download Sample
            </Button>

            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputs.current[activeConfig.key]?.click()}
              sx={primaryBtnSx}
            >
              Bulk Upload
            </Button>

            <input
              hidden
              type="file"
              accept=".xlsx,.xls"
              ref={(el) => {
                fileInputs.current[activeConfig.key] = el;
              }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                handleBulkUpload(activeConfig, file);
                e.target.value = "";
              }}
            />
          </Box>
        </Box>

        <Box sx={{ p: 2, bgcolor: BG }}>
          <Paper elevation={0} sx={surfaceSx}>
            <Tabs
              value={tab}
              onChange={(_, v) => {
                setTab(v);
                setEditState({ key: null, id: null });
                setAddDialogOpen(false);
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={tabsSx}
            >
              {MASTER_CONFIGS.map((cfg) => (
                <Tab
                  key={cfg.key}
                  label={cfg.label}
                  icon={cfg.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>

            <Box sx={{ p: 2 }}>{renderTab(activeConfig)}</Box>
          </Paper>
        </Box>
      </Paper>


      <Dialog
        open={addDialogOpen}
        onClose={closeAddDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
            boxShadow: "0 18px 55px rgba(15,23,42,.22)",
            backgroundColor: "white",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ ...headerSx, borderRadius: 0, px: 3, py: 2 }}>
            <Typography sx={{ fontWeight: 900 }}>
              Add {activeConfig.label}
            </Typography>
            <Typography sx={{ fontSize: ".82rem", opacity: .9 }}>
              Enter the new {activeConfig.label.toLowerCase()} details.
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ backgroundColor: BG, p: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: 2.5,
              borderRadius: 2.5,
              border: `1px solid ${BORDER}`,
              borderTop: `3px solid ${THEME_ORANGE}`,
              backgroundColor: "white",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(180px, 1fr))",
                },
                gap: 2,
              }}
            >
              {activeConfig.fields.map((field) => (
                <Box key={field.key}>
                  {renderField(
                    activeConfig,
                    field,
                    newRows[activeConfig.key]?.[field.key],
                    (v) => updateNewRow(activeConfig, field.key, v)
                  )}
                </Box>
              ))}

              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Checkbox
                  checked={
                    Number(newRows[activeConfig.key]?.IsActive ?? 1) === 1
                  }
                  onChange={(e) =>
                    updateNewRow(
                      activeConfig,
                      "IsActive",
                      e.target.checked ? 1 : 0
                    )
                  }
                  sx={{
                    color: BLUE,
                    "&.Mui-checked": { color: THEME_ORANGE },
                  }}
                />
                <Typography variant="body2">Active</Typography>
              </Stack>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: "white",
            borderTop: `1px solid ${BORDER}`,
          }}
        >
          <Button onClick={closeAddDialog} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => addRow(activeConfig)}
            sx={primaryBtnSx}
          >
            Add {activeConfig.label}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!msg.text}
        autoHideDuration={4000}
        onClose={() => setMsg((m) => ({ ...m, text: "" }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={msg.type}
          onClose={() => setMsg((m) => ({ ...m, text: "" }))}
          sx={{ borderRadius: 2 }}
        >
          {msg.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
